import { Button, Field, Input } from '@quickreport/ui';
import { useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { MAX_PIN, MIN_PIN, pinProblem } from '../../local-db/pin';

/** Set, change or remove the PIN that opens LabView on this computer without internet. */
export function OfflinePinPanel() {
  const { pinSet, setPin, removePin } = useAuth();
  const [pin, setP] = useState('');
  const [again, setAgain] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  async function save() {
    const problem = pinProblem(pin) ?? (pin !== again ? 'The two PINs are different.' : null);
    if (problem) return setNote({ tone: 'error', text: problem });
    try {
      await setPin(pin);
      setP('');
      setAgain('');
      setNote({
        tone: 'ok',
        text: 'PIN saved. Use the lock button in the menu, or open the app without internet, and enter it.',
      });
    } catch (err) {
      setNote({
        tone: 'error',
        text: err instanceof Error ? err.message : 'Could not save the PIN.',
      });
    }
  }

  return (
    <section id="pin" className="grid gap-3 rounded-lg border border-line bg-paper p-4">
      <h2 className="text-base font-semibold">Offline PIN</h2>
      <p className="text-[13px] text-slate">
        With a PIN, anyone using this computer must enter it to open LabView when there is no
        internet, after you lock it, or when your sign-in has ended. Your lab's data stays on this
        computer so you can keep working. The PIN guards the screen; it is not a replacement for
        locking the computer itself.
      </p>
      <div className="grid max-w-md grid-cols-2 gap-3">
        <Field label={pinSet ? 'New PIN' : 'PIN'} hint={`${MIN_PIN} to ${MAX_PIN} digits`}>
          <Input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={pin}
            onChange={(e) => setP(e.target.value.replace(/\D/g, '').slice(0, MAX_PIN))}
          />
        </Field>
        <Field label="Again">
          <Input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={again}
            onChange={(e) => setAgain(e.target.value.replace(/\D/g, '').slice(0, MAX_PIN))}
          />
        </Field>
      </div>
      <div className="flex gap-2">
        <Button
          tone="primary"
          size="sm"
          onClick={() => void save()}
          disabled={pin.length < MIN_PIN}
        >
          {pinSet ? 'Change PIN' : 'Set PIN'}
        </Button>
        {pinSet ? (
          <Button
            size="sm"
            onClick={async () => {
              await removePin();
              setNote({ tone: 'ok', text: 'PIN removed.' });
            }}
          >
            Remove PIN
          </Button>
        ) : null}
      </div>
      {note ? (
        <p
          role={note.tone === 'error' ? 'alert' : 'status'}
          className={`text-[13px] ${note.tone === 'ok' ? 'text-ok' : 'text-flag'}`}
        >
          {note.text}
        </p>
      ) : null}
    </section>
  );
}
